// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.

// /review — 30-day founding member review form
// Linked from the reminder email at day 30.
// Saves review to founding_agreements table.

import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { GuardRokIcon } from '../components/GuardRokLogo'
import { CheckCircle, Star } from 'lucide-react'

export default function FoundingReview() {
  const [searchParams] = useSearchParams()
  const agreementId = searchParams.get('id') ?? ''

  const [agreement, setAgreement] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [alreadyDone, setAlreadyDone] = useState(false)

  const [q1, setQ1] = useState('')
  const [q2, setQ2] = useState('')
  const [q3, setQ3] = useState('')
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)

  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!agreementId) {
      setNotFound(true)
      setLoading(false)
      return
    }
    supabase
      .from('founding_agreements')
      .select('id, full_name, firm_name, review_submitted, review_consent')
      .eq('id', agreementId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) {
          setNotFound(true)
        } else if (data.review_submitted) {
          setAlreadyDone(true)
        } else {
          setAgreement(data)
        }
        setLoading(false)
      })
  }, [agreementId])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!q1.trim() || !q2.trim() || !q3.trim() || rating === 0) return
    setSubmitting(true)
    setError(null)
    const { error: err } = await supabase
      .from('founding_agreements')
      .update({
        review_submitted: true,
        review_submitted_at: new Date().toISOString(),
        review_q1: q1.trim(),
        review_q2: q2.trim(),
        review_q3: q3.trim(),
        review_rating: rating,
      })
      .eq('id', agreementId)
    if (err) {
      setError(err.message)
      setSubmitting(false)
      return
    }
    setDone(true)
    setSubmitting(false)
  }

  if (loading) return <Loader />

  if (notFound)
    return (
      <Shell>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>🔍</div>
          <h2 style={{ color: '#f1f5f9', fontWeight: 800, marginBottom: 10 }}>
            Agreement not found
          </h2>
          <p style={{ color: '#64748b', fontSize: 14 }}>
            Check the link in your email or contact hello@guardrok.com
          </p>
        </div>
      </Shell>
    )

  if (alreadyDone)
    return (
      <Shell>
        <div style={{ textAlign: 'center' }}>
          <CheckCircle size={40} color="#22c55e" style={{ margin: '0 auto 16px' }} />
          <h2 style={{ color: '#f1f5f9', fontWeight: 800, marginBottom: 10 }}>
            Review already submitted
          </h2>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
            Thank you — your review is on file. We'll be in touch if we'd like to publish it.
          </p>
          <Link to="/dashboard" style={{ color: '#94a3b8', fontSize: 13 }}>
            Back to dashboard →
          </Link>
        </div>
      </Shell>
    )

  if (done)
    return (
      <Shell>
        <div style={{ textAlign: 'center' }}>
          <CheckCircle size={40} color="#22c55e" style={{ margin: '0 auto 16px' }} />
          <h2 style={{ color: '#f1f5f9', fontWeight: 800, marginBottom: 12 }}>
            Thank you, {agreement?.full_name?.split(' ')[0]}!
          </h2>
          <p
            style={{
              color: '#94a3b8',
              fontSize: 14,
              lineHeight: 1.7,
              maxWidth: 420,
              margin: '0 auto 20px',
            }}
          >
            Your review is saved. We read every one personally.
            {agreement?.review_consent !== 'none' &&
              " If we'd like to feature it on the site, we'll reach out for final confirmation first."}
          </p>
          <Link
            to="/dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#E63946',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              padding: '10px 20px',
              borderRadius: 8,
              textDecoration: 'none',
            }}
          >
            Back to dashboard →
          </Link>
        </div>
      </Shell>
    )

  return (
    <Shell>
      <div style={{ marginBottom: 28, textAlign: 'center' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#475569',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          30-day founding member review
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 900, letterSpacing: '-0.5px', marginBottom: 8 }}>
          How is GuardRok working for you?
        </h1>
        <p style={{ color: '#64748b', fontSize: 14 }}>
          3 questions · 90 seconds · {agreement?.firm_name}
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Star rating */}
        <div
          style={{
            background: '#080f1a',
            border: '1px solid #1e293b',
            borderRadius: 12,
            padding: 20,
          }}
        >
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#94a3b8',
              display: 'block',
              marginBottom: 12,
            }}
          >
            Overall rating
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
              >
                <Star
                  size={28}
                  fill={(hover || rating) >= n ? '#f59e0b' : 'none'}
                  color={(hover || rating) >= n ? '#f59e0b' : '#374151'}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Q1 */}
        <div
          style={{
            background: '#080f1a',
            border: '1px solid #1e293b',
            borderRadius: 12,
            padding: 20,
          }}
        >
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#94a3b8',
              display: 'block',
              marginBottom: 8,
            }}
          >
            1. What almost happened — or did happen — before GuardRok?
          </label>
          <p style={{ fontSize: 11, color: '#475569', marginBottom: 10 }}>
            e.g. "An associate nearly pasted client wire instructions into ChatGPT"
          </p>
          <textarea
            value={q1}
            onChange={e => setQ1(e.target.value)}
            required
            rows={3}
            placeholder="Tell us the situation..."
            style={{
              width: '100%',
              background: '#0f172a',
              border: '1px solid #374151',
              borderRadius: 8,
              padding: '10px 14px',
              color: '#f1f5f9',
              fontSize: 13,
              resize: 'vertical',
              fontFamily: 'system-ui',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Q2 */}
        <div
          style={{
            background: '#080f1a',
            border: '1px solid #1e293b',
            borderRadius: 12,
            padding: 20,
          }}
        >
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#94a3b8',
              display: 'block',
              marginBottom: 8,
            }}
          >
            2. What does GuardRok do for your firm — in one sentence?
          </label>
          <p style={{ fontSize: 11, color: '#475569', marginBottom: 10 }}>
            Your own words. These are the most valuable sentences on our website.
          </p>
          <textarea
            value={q2}
            onChange={e => setQ2(e.target.value)}
            required
            rows={2}
            placeholder="GuardRok..."
            style={{
              width: '100%',
              background: '#0f172a',
              border: '1px solid #374151',
              borderRadius: 8,
              padding: '10px 14px',
              color: '#f1f5f9',
              fontSize: 13,
              resize: 'vertical',
              fontFamily: 'system-ui',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Q3 */}
        <div
          style={{
            background: '#080f1a',
            border: '1px solid #1e293b',
            borderRadius: 12,
            padding: 20,
          }}
        >
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#94a3b8',
              display: 'block',
              marginBottom: 8,
            }}
          >
            3. Would you recommend GuardRok to another firm? Why?
          </label>
          <textarea
            value={q3}
            onChange={e => setQ3(e.target.value)}
            required
            rows={3}
            placeholder="Yes / No and why..."
            style={{
              width: '100%',
              background: '#0f172a',
              border: '1px solid #374151',
              borderRadius: 8,
              padding: '10px 14px',
              color: '#f1f5f9',
              fontSize: 13,
              resize: 'vertical',
              fontFamily: 'system-ui',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {error && (
          <div
            style={{
              background: '#1c0a0a',
              border: '1px solid #7f1d1d',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 13,
              color: '#f87171',
            }}
          >
            ⚠ {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !q1.trim() || !q2.trim() || !q3.trim() || rating === 0}
          style={{
            background: '#E63946',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            padding: '13px',
            borderRadius: 10,
            border: 'none',
            cursor: 'pointer',
            opacity: !q1.trim() || !q2.trim() || !q3.trim() || rating === 0 ? 0.5 : 1,
          }}
        >
          {submitting ? 'Submitting…' : 'Submit my review →'}
        </button>

        <p style={{ fontSize: 11, color: '#374151', textAlign: 'center', lineHeight: 1.6 }}>
          Publishing preference on file:{' '}
          <strong style={{ color: '#6b7280' }}>
            {agreement?.review_consent === 'full_name'
              ? 'Full name + firm'
              : agreement?.review_consent === 'anonymous'
                ? 'Anonymous'
                : 'Do not publish'}
          </strong>
          . Email hello@guardrok.com to change.
        </p>
      </form>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#030712',
        color: '#fff',
        fontFamily: 'system-ui,sans-serif',
      }}
    >
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '20px 48px',
          borderBottom: '1px solid #0f172a',
        }}
      >
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}
        >
          <GuardRokIcon size={28} />
          <span style={{ fontWeight: 800, fontSize: 15, color: '#fff' }}>
            Guard<span style={{ color: '#E63946' }}>Rok</span>
            <sup style={{ fontSize: 8, color: '#6b7280' }}>™</sup>
          </span>
        </Link>
      </nav>
      <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 24px' }}>{children}</div>
    </div>
  )
}

function Loader() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#030712',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ color: '#475569', fontSize: 13 }}>Loading…</div>
    </div>
  )
}
